import {
    type MutableRefObject,
    type PointerEvent,
    useCallback,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { flushSync } from "react-dom";
import { useDraggable } from "@dnd-kit/core";
import type { RowData } from "@tanstack/react-table";
import {
    CELL_CONTROL_SELECTOR,
    isInsideMatching,
} from "../../cells/cellControls";
import type { GridController } from "../../focus/gridController";
import type { DataGridInstance } from "../../types";
import { getRowSelectionDisplay } from "../selection/rowSelection";
import type { DraggableApi } from "./dragKind";

/** Cell content that keeps its own presses: a press on it never drags the row out. */
const NO_DRAG_OUT_SELECTOR = [
    CELL_CONTROL_SELECTOR,
    "[data-dt-controls]",
    "[data-dt-editor]",
    "[data-dt-fill-handle]",
    "label",
    "[contenteditable='true']",
    "[role='combobox']",
    "[role='link']",
    "[role='switch']",
].join(", ");

const NO_DRAG_DATA = {};

type RowDragOutApi = Omit<DraggableApi, "setActivatorNodeRef"> & {
    setData: (data: object) => void;
};

type RowDragOutApiRef = MutableRefObject<RowDragOutApi | null>;

/**
 * The single draggable every row drags out through; a press re-points it at
 * the pressed row and hands it that row's payload. Render it outside the
 * grid's own `GridDnd` so it joins the app's `DndContext`, not the table's.
 */
export const RowDragOutSource = ({
    apiRef,
    controller,
}: {
    apiRef: RowDragOutApiRef;
    controller: GridController;
}) => {
    const id = useId();
    const [data, setData] = useState<object>(NO_DRAG_DATA);
    const { setNodeRef, listeners, isDragging } = useDraggable({
        id: `dg-row-drag-out:${id}`,
        data,
    });
    useLayoutEffect(() => {
        apiRef.current = { setNodeRef, listeners, setData };
    }, [apiRef, listeners, setNodeRef]);
    useLayoutEffect(() => {
        const { viewport } = controller;
        if (!isDragging || !viewport) {
            return undefined;
        }
        viewport.setAttribute("data-dragging", "");
        return () => viewport.removeAttribute("data-dragging");
    }, [controller, isDragging]);
    return null;
};

/**
 * Starts a row drag-out from a press anywhere on a body row, delegated on
 * the grid element. Presses on cell controls (not the grid's own ancestors,
 * such as an upload drop zone), popups the cells portal out,
 * separator and group rows, and presses another handler already claimed
 * (a column resize) stay theirs.
 */
export const useRowDragOut = <TData extends RowData>(
    instance: DataGridInstance<TData>,
) => {
    const sourceRef = useRef<RowDragOutApi | null>(null);
    const { table, optionsRef, rowKinds } = instance;

    const onPointerDown = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            const source = sourceRef.current;
            const { rowDragOut } = optionsRef.current;
            const { target, currentTarget } = event;
            if (
                !source ||
                !rowDragOut ||
                event.defaultPrevented ||
                event.button !== 0 ||
                !(target instanceof Element) ||
                !currentTarget.contains(target)
            ) {
                return;
            }
            if (
                isInsideMatching({
                    target,
                    container: currentTarget,
                    selector: NO_DRAG_OUT_SELECTOR,
                })
            ) {
                return;
            }
            const rowElement = target.closest<HTMLElement>(
                "[role='row'][data-row-id]",
            );
            const rowId = rowElement?.dataset.rowId;
            const row = rowId ? table.getRowModel().rowsById[rowId] : undefined;
            if (
                !rowElement ||
                !row ||
                row.getIsGrouped() ||
                rowKinds.isSeparator(row.original)
            ) {
                return;
            }
            const data = rowDragOut.getRowDragData(row.original, {
                isSelected: getRowSelectionDisplay(row) === "all",
            });
            if (data === null) {
                return;
            }
            // Without an activation constraint dnd-kit starts the drag inside
            // onPointerDown, reading the data its layout effect last stored.
            flushSync(() => source.setData(data));
            source.setNodeRef(rowElement);
            source.listeners?.onPointerDown?.(event);
        },
        [optionsRef, rowKinds, table],
    );

    return { sourceRef, onPointerDown };
};
